// Documentation lives in the repository as components, so it versions with the code it describes
// and there is no external site to keep in step (APP_TEMPLATE.md §8).
//
// Adding a page: write the component, add one entry here, and it appears in the nav and routes.

import type { ComponentType } from "react";
import { GettingStarted } from "./GettingStarted";
import { ModelsAndParameters } from "./ModelsAndParameters";
import { EditingAndLineage } from "./EditingAndLineage";
import { YourImages } from "./YourImages";
import { LimitsAndCosts } from "./LimitsAndCosts";

export interface DocPage {
  slug: string;
  title: string;
  /** One line, shown under the title on the page itself. */
  summary: string;
  component: ComponentType;
}

export interface DocSection {
  id: string;
  title: string;
  pages: DocPage[];
}

const gettingStarted: DocPage = {
  slug: "getting-started",
  title: "Getting started",
  summary: "Boards, the composer, and how Darkroom decides whether you are generating or editing.",
  component: GettingStarted,
};

const modelsAndParameters: DocPage = {
  slug: "models-and-parameters",
  title: "Models and parameters",
  summary: "Where the controls come from, and what they mean.",
  component: ModelsAndParameters,
};

const editingAndLineage: DocPage = {
  slug: "editing-and-lineage",
  title: "Editing and lineage",
  summary: "Using an output as the next input, and reading where a frame came from.",
  component: EditingAndLineage,
};

const yourImages: DocPage = {
  slug: "your-images",
  title: "Your images",
  summary: "The library, and what deleting does and does not remove.",
  component: YourImages,
};

const limitsAndCosts: DocPage = {
  slug: "limits-and-costs",
  title: "Limits and costs",
  summary: "Background work, retries, and the fact that nothing meters spend.",
  component: LimitsAndCosts,
};

export const docSections: DocSection[] = [
  {
    id: "using",
    title: "Using Darkroom",
    pages: [gettingStarted, modelsAndParameters, editingAndLineage],
  },
  {
    id: "keeping",
    title: "Your work",
    pages: [yourImages, limitsAndCosts],
  },
];

/** Flattened in nav order, which is also route order. */
export const docPages: DocPage[] = docSections.flatMap((section) => section.pages);

/** Where `/docs` with no slug lands. */
export const defaultDocPage: DocPage = gettingStarted;

export function findDocPage(slug: string | undefined): DocPage | undefined {
  if (!slug) return undefined;
  return docPages.find((page) => page.slug === slug);
}
